/**
 * Transactions on a phone (mobile M3): a list grouped by day, not a table.
 *
 * Each day is a group with a header ("Mon · 3 Nov", that day's spend), and a
 * single floating bar under the top bar always names the day at the top of
 * the screen, so the date never scrolls away. Still virtualized, by day group
 * and against the page's own scroll: only the groups near the screen are in
 * the DOM. (CSS `sticky` inside the virtualizer's translated groups drew the
 * headers over the wrong rows in Chromium, hence the one floating bar.) The
 * server pages the history, so tens of thousands of rows never reach here.
 *
 * Tapping a row opens `TxnSheet` with everything the row has no room for.
 * Provisional rows (from alerts) wear blue and dashed, as everywhere.
 */

import { useWindowVirtualizer } from "@tanstack/react-virtual";
import { Clock } from "lucide-react";
import * as React from "react";

import { CardSwatch } from "@/components/ui/primitives";
import { type Card, type Transaction } from "@/lib/api/client";
import { cn } from "@/lib/cn";
import { formatMoney } from "@/lib/format";

import { TxnSheet } from "./TxnSheet";

export type DayGroup = { date: string; rows: Transaction[]; spend: number };

/** Rows (already newest-first from the server) split into days, keeping order. */
export function groupByDay(rows: Transaction[]): DayGroup[] {
  const groups: DayGroup[] = [];
  for (const t of rows) {
    let g = groups[groups.length - 1];
    if (!g || g.date !== t.txn_date) {
      g = { date: t.txn_date, rows: [], spend: 0 };
      groups.push(g);
    }
    g.rows.push(t);
    // Paise as integers, so a day's total never drifts by a float's rounding.
    if (t.direction === "debit") g.spend += Math.round(Number(t.amount) * 100);
  }
  return groups;
}

const WEEKDAY = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTH = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** "Mon · 3 Nov", or "Today" / "Yesterday" against `today` (YYYY-MM-DD). */
export function dayLabel(date: string, today: string): string {
  if (date === today) return "Today";
  const d = new Date(`${date}T00:00:00`);
  const t = new Date(`${today}T00:00:00`);
  if ((t.getTime() - d.getTime()) / 86_400_000 === 1) return "Yesterday";
  const year = d.getFullYear() !== t.getFullYear() ? ` ${d.getFullYear()}` : "";
  return `${WEEKDAY[d.getDay()]} · ${d.getDate()} ${MONTH[d.getMonth()]}${year}`;
}

function localToday(): string {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

export function PhoneTxnList({ rows, cards }: { rows: Transaction[]; cards: Card[] }) {
  const groups = React.useMemo(() => groupByDay(rows), [rows]);
  const byId = React.useMemo(() => new Map(cards.map((c) => [c.id, c])), [cards]);
  const [open, setOpen] = React.useState<Transaction | null>(null);
  const today = localToday();

  const listRef = React.useRef<HTMLDivElement>(null);
  const [offset, setOffset] = React.useState(0);
  React.useLayoutEffect(() => {
    setOffset(listRef.current ? listRef.current.getBoundingClientRect().top + window.scrollY : 0);
  }, []);

  // The day at the top of the screen, for the floating bar.
  const [current, setCurrent] = React.useState<DayGroup | null>(null);
  React.useEffect(() => {
    let frame = 0;
    const update = () => {
      frame = 0;
      const el = listRef.current;
      if (!el) return;
      const bar = (document.querySelector("header")?.getBoundingClientRect().bottom ?? 56) + 1;
      const sections = el.querySelectorAll<HTMLElement>("section[data-index]");
      let found: DayGroup | null = null;
      for (const sec of sections) {
        const r = sec.getBoundingClientRect();
        if (r.top <= bar && r.bottom > bar + 8) {
          found = groups[Number(sec.dataset.index)] ?? null;
          break;
        }
      }
      setCurrent(found);
    };
    const onScroll = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };
    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (frame) cancelAnimationFrame(frame);
    };
  }, [groups]);

  const virtualizer = useWindowVirtualizer({
    count: groups.length,
    estimateSize: (i) => 40 + (groups[i]?.rows.length ?? 1) * 64,
    overscan: 4,
    scrollMargin: offset,
  });

  return (
    <>
      {current && (
        <div className="fixed inset-x-0 top-[calc(3.5rem+var(--inset-top))] z-20 border-b border-hairline bg-base/90 px-5 backdrop-blur-xl md:hidden">
          <DayHeader group={current} today={today} />
        </div>
      )}
      <div ref={listRef} style={{ height: virtualizer.getTotalSize(), position: "relative" }}>
        {virtualizer.getVirtualItems().map((item) => {
          const g = groups[item.index]!;
          return (
            <section
              key={g.date}
              data-index={item.index}
              ref={virtualizer.measureElement}
              className="absolute inset-x-0"
              style={{ transform: `translateY(${item.start - virtualizer.options.scrollMargin}px)` }}
            >
              <div className="border-b border-hairline px-1">
                <DayHeader group={g} today={today} />
              </div>
              <ul>
                {g.rows.map((t) => (
                  <li key={t.id}>
                    <TxnRow t={t} card={byId.get(t.card_id)} onOpen={() => setOpen(t)} />
                  </li>
                ))}
              </ul>
            </section>
          );
        })}
      </div>
      <TxnSheet
        txn={open}
        card={open ? byId.get(open.card_id) : undefined}
        onOpenChange={(o) => !o && setOpen(null)}
      />
    </>
  );
}

function DayHeader({ group, today }: { group: DayGroup; today: string }) {
  return (
    <h3 className="flex h-10 items-center justify-between text-xs">
      <span className="font-medium text-ink">{dayLabel(group.date, today)}</span>
      {group.spend > 0 && (
        <span className="tabular-nums text-faint">{formatMoney((group.spend / 100).toFixed(2))}</span>
      )}
    </h3>
  );
}

function TxnRow({ t, card, onOpen }: { t: Transaction; card: Card | undefined; onOpen: () => void }) {
  const credit = t.direction === "credit";
  return (
    <button
      type="button"
      onClick={onOpen}
      className={cn(
        "flex min-h-16 w-full items-center gap-3 border-b border-hairline px-1 py-2.5 text-left active:bg-raised",
        t.is_provisional && "border-dashed",
      )}
    >
      <CardSwatch theme={card?.theme ?? null} />
      <span className="min-w-0 flex-1">
        <span className={cn("block truncate text-[15px]", t.is_provisional ? "text-muted" : "text-ink")}>
          {t.merchant || t.description_raw}
        </span>
        <span className="mt-0.5 flex items-center gap-1.5 truncate text-xs text-faint">
          {t.is_provisional && (
            <span className="inline-flex shrink-0 items-center gap-1 rounded border border-dashed border-live/50 px-1 text-[10.5px] text-live">
              <Clock className="h-2.5 w-2.5" aria-hidden />
              alert
            </span>
          )}
          <span className="truncate">{card ? card.nickname || card.product_name || card.issuer : "—"}</span>
        </span>
      </span>
      <span
        className={cn(
          "shrink-0 font-medium tabular-nums",
          t.is_provisional ? "text-live" : credit ? "text-paid" : "text-ink",
        )}
      >
        {credit ? "+" : ""}
        {formatMoney(t.amount)}
      </span>
    </button>
  );
}
